import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";

import { toVisibility } from "../../../hooks/useStationVisibility";
import { excludeStations, listExclusions } from "../../../services/stationExclusions";
import { getStationsApi } from "../../../services/stations";
import { ExclusionResult } from "../../../types/alerting";
import StationVisibilityTag from "../StationStatusDetail/StationVisibilityTag";
import {
  MAX_NOTE_LENGTH,
  MAX_STATIONS_PER_REQUEST,
  REFUSED_MESSAGE,
  errorMessageOf,
  isRefusal,
} from "./exclusions";

interface Station {
  id: string;
  name?: string | null;
  city?: string | null;
  publish?: boolean | null;
}

type LoadState = "loading" | "ready" | "refused" | "failed";

const plural = (count: number) => `${count} station${count === 1 ? "" : "s"}`;

const idsWith = (results: ExclusionResult[], outcome: ExclusionResult["outcome"]) =>
  results.filter((r) => r.outcome === outcome).map((r) => r.stationId);

const ExclusionSummary = ({ results }: { results: ExclusionResult[] }) => {
  const added = idsWith(results, "ADDED").length;
  const already = idsWith(results, "ALREADY_EXCLUDED");
  const unknown = idsWith(results, "UNKNOWN_STATION");

  return (
    <Message
      severity={already.length || unknown.length ? "warn" : "success"}
      className="w-full justify-content-start"
      content={
        <div className="flex flex-column gap-1">
          <span>{plural(added)} added to the exclusion list.</span>
          {already.length > 0 && <span>Already excluded: {already.join(", ")}.</span>}
          {unknown.length > 0 && <span>Not found, so not excluded: {unknown.join(", ")}.</span>}
        </div>
      }
    />
  );
};

const AddStationExclusionsPage = () => {
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>("loading");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [stations, setStations] = useState<Station[]>([]);
  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");
  const [selection, setSelection] = useState<Station[]>([]);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [results, setResults] = useState<ExclusionResult[] | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([getStationsApi("COCHARGE"), listExclusions()])
      .then(([data, exclusions]) => {
        if (!active) return;
        setStations(data ?? []);
        setExcluded(new Set(exclusions.map((e) => e.stationId)));
        setState("ready");
      })
      .catch((e) => {
        if (!active) return;
        setLoadError(errorMessageOf(e));
        setState(isRefusal(e) ? "refused" : "failed");
      });
    return () => {
      active = false;
    };
  }, []);

  const count = selection.length;
  const isNoteTooLong = note.length > MAX_NOTE_LENGTH;
  const isTooMany = count > MAX_STATIONS_PER_REQUEST;
  const canSubmit = count > 0 && !isTooMany && !isNoteTooLong && !submitting;

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);
    setResults(null);
    try {
      const outcome = await excludeStations(
        selection.map((s) => s.id),
        note.trim() || null,
      );
      setResults(outcome);
      setExcluded((previous) => {
        const next = new Set(previous);
        outcome
          .filter((r) => r.outcome !== "UNKNOWN_STATION")
          .forEach((r) => next.add(r.stationId));
        return next;
      });
      setSelection([]);
      setNote("");
    } catch (e) {
      setSubmitError(
        isRefusal(e) ? REFUSED_MESSAGE : `Could not exclude the stations: ${errorMessageOf(e)}`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const renderBody = () => {
    if (state === "loading") {
      return (
        <div className="flex align-items-center gap-3 py-5" data-testid="view-state-loading">
          <ProgressSpinner style={{ width: "2rem", height: "2rem" }} strokeWidth="4" />
          <span className="text-color-secondary">Loading…</span>
        </div>
      );
    }
    if (state === "refused") {
      return <Message severity="warn" text={REFUSED_MESSAGE} />;
    }
    if (state === "failed") {
      return <Message severity="error" text={`Could not load the stations: ${loadError}`} />;
    }

    return (
      <>
        {results && (
          <div className="flex flex-column align-items-start gap-3 mb-4" data-testid="exclusion-summary">
            <ExclusionSummary results={results} />
            <Button
              label="Back to excluded stations"
              icon="pi pi-arrow-left"
              onClick={() => navigate("/alerting/exclusions")}
            />
          </div>
        )}

        <div className="flex mb-3">
          <span className="p-input-icon-left w-full md:w-25rem">
            <i className="pi pi-search" />
            <InputText
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by ID, name or city"
              aria-label="Search stations"
              className="w-full"
            />
          </span>
        </div>

        <DataTable
          value={stations}
          dataKey="id"
          selectionMode="checkbox"
          selection={selection}
          onSelectionChange={(e) => setSelection(e.value as Station[])}
          isDataSelectable={(e) => !excluded.has(e.data?.id)}
          selectionAriaLabel="id"
          globalFilter={search}
          globalFilterFields={["id", "name", "city"]}
          paginator
          rows={25}
          rowsPerPageOptions={[25, 50, 100]}
          emptyMessage="No stations found."
          className="p-datatable-sm"
        >
          <Column selectionMode="multiple" headerStyle={{ width: "3rem" }} />
          <Column field="id" header="ID" sortable />
          <Column field="name" header="Name" sortable body={(s: Station) => s.name ?? "—"} />
          <Column field="city" header="City" sortable body={(s: Station) => s.city ?? "—"} />
          <Column
            header="Visibility"
            body={(s: Station) => (
              <StationVisibilityTag visibility={toVisibility(s.publish)} excluded={excluded.has(s.id)} />
            )}
          />
        </DataTable>

        <div className="flex flex-column gap-2 mt-4 md:w-30rem">
          <label htmlFor="exclusion-note" className="font-semibold">
            Note (optional)
          </label>
          <InputTextarea
            id="exclusion-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            autoResize
            className={isNoteTooLong ? "p-invalid w-full" : "w-full"}
            placeholder="e.g. waiting for electrician"
          />
          <small className={isNoteTooLong ? "p-error" : "text-color-secondary"}>
            {`${note.length}/${MAX_NOTE_LENGTH}`}
          </small>
        </div>

        {submitError && <Message severity="error" text={submitError} className="mt-3" />}

        <div className="flex flex-wrap align-items-center gap-3 mt-3">
          <Button
            label={`Exclude ${plural(count)}`}
            icon="pi pi-ban"
            severity="danger"
            loading={submitting}
            disabled={!canSubmit}
            onClick={handleSubmit}
          />
          {isTooMany && (
            <small className="p-error">
              At most {MAX_STATIONS_PER_REQUEST} stations can be excluded at once.
            </small>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="surface-section px-4 py-5 md:px-6 lg:px-8">
      <div className="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center gap-3 mb-4">
        <div>
          <div className="text-2xl font-semibold">Exclude stations from alerting</div>
          <p className="text-color-secondary mt-2 mb-0">
            Owners of excluded stations get no timeout alerts, reminders or back-online messages.
          </p>
        </div>
        <Button
          label="All excluded stations"
          icon="pi pi-list"
          outlined
          onClick={() => navigate("/alerting/exclusions")}
        />
      </div>

      {renderBody()}
    </div>
  );
};

export default AddStationExclusionsPage;
